import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Package, FolderTree, ListChecks, Check } from "lucide-react";

const KIND_HINT = {
  catalog: "Seçilen kataloğun ve tüm alt klasörlerinin altında satılan her ürün bu satıra sayılır.",
  product: "Sadece seçilen tek ürünün satışları sayılır.",
  products: "İşaretlediğiniz tüm ürünlerin satışları bu satırda toplanır.",
};

export function catPath(c, catalogs) {
  const parts = [c.name];
  let p = catalogs.find((x) => x.id === c.parent_id);
  while (p) { parts.unshift(p.name); p = catalogs.find((x) => x.id === p.parent_id); }
  return parts.join(" › ");
}

function OptionRow({ active, onClick, icon: Icon, label, sub, testId, multi }) {
  return (
    <button type="button" onClick={onClick} data-testid={testId}
            className={`w-full text-left px-3 py-2.5 rounded-lg flex items-center gap-2.5 transition-colors ${active ? "bg-blue-600/20 border border-blue-500/40" : "hover:bg-slate-800/70 border border-transparent"}`}>
      {multi ? (
        <span className={`h-4 w-4 rounded border flex items-center justify-center shrink-0 ${active ? "bg-blue-600 border-blue-500" : "border-slate-500"}`}>
          {active && <Check size={12} className="text-white" />}
        </span>
      ) : <Icon size={15} className="text-blue-300 shrink-0" />}
      <span className="text-sm font-semibold flex-1">{label}</span>
      {sub && <span className="text-[10px] font-mono text-slate-500">{sub}</span>}
      {active && !multi && <Check size={15} className="text-blue-300" />}
    </button>
  );
}

export function LineDialog({ open, initial, products, catalogs, onClose, onSave }) {
  const [label, setLabel] = useState("");
  const [kind, setKind] = useState("catalog");
  const [refId, setRefId] = useState(null);
  const [multi, setMulti] = useState([]);
  const [q, setQ] = useState("");

  useEffect(() => {
    if (!open) return;
    setLabel(initial?.label || "");
    setKind(initial?.kind || "catalog");
    setRefId(initial?.ref_id || null);
    setMulti(initial?.product_ids || []);
    setQ("");
  }, [open, initial]);

  const active = products.filter((p) => p.is_active !== false);
  const match = (t) => t.toLowerCase().includes(q.toLowerCase());
  const toggle = (id) => setMulti((m) => (m.includes(id) ? m.filter((x) => x !== id) : [...m, id]));
  const valid = kind === "products" ? multi.length > 0 : !!refId;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-slate-900 border-slate-800 max-w-lg max-h-[90vh] flex flex-col" data-testid="line-dialog">
        <DialogHeader><DialogTitle>{initial?.id ? "Hedef Satırını Düzenle" : "Hedef Satırı Ekle"}</DialogTitle></DialogHeader>
        <div>
          <Label className="text-xs">Satır Adı (tabloda görünecek)</Label>
          <Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Örn: Faturalı" className="bg-slate-950/50 border-slate-700 mt-1" data-testid="line-label" />
        </div>
        <Tabs value={kind} onValueChange={(v) => { setKind(v); setRefId(null); }}>
          <TabsList className="bg-slate-950/60 border border-slate-800 w-full">
            <TabsTrigger value="catalog" className="flex-1 text-xs" data-testid="line-kind-catalog"><FolderTree size={13} className="mr-1" /> Ürün Gamı</TabsTrigger>
            <TabsTrigger value="product" className="flex-1 text-xs" data-testid="line-kind-product"><Package size={13} className="mr-1" /> Tek Ürün</TabsTrigger>
            <TabsTrigger value="products" className="flex-1 text-xs" data-testid="line-kind-products"><ListChecks size={13} className="mr-1" /> Toplu Ürün</TabsTrigger>
          </TabsList>
        </Tabs>
        <p className="text-[11px] text-slate-500 -mt-1">{KIND_HINT[kind]}</p>
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Ara..." className="bg-slate-950/50 border-slate-700" data-testid="line-search" />
        <div className="flex-1 overflow-y-auto space-y-1 min-h-[180px] max-h-[40vh] pr-1">
          {kind === "catalog" && catalogs.filter((c) => match(catPath(c, catalogs))).map((c) => (
            <OptionRow key={c.id} active={refId === c.id} onClick={() => setRefId(c.id)} icon={FolderTree}
                       label={catPath(c, catalogs)} testId={`line-opt-${c.id}`} />
          ))}
          {kind === "product" && active.filter((p) => match(p.name)).map((p) => (
            <OptionRow key={p.id} active={refId === p.id} onClick={() => setRefId(p.id)} icon={Package}
                       label={p.name} sub={p.sku} testId={`line-opt-${p.id}`} />
          ))}
          {kind === "products" && active.filter((p) => match(p.name)).map((p) => (
            <OptionRow key={p.id} multi active={multi.includes(p.id)} onClick={() => toggle(p.id)}
                       label={p.name} sub={p.sku} testId={`line-opt-${p.id}`} />
          ))}
        </div>
        {kind === "products" && <p className="text-xs text-blue-300" data-testid="line-multi-count">{multi.length} ürün seçildi</p>}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="border-slate-700">İptal</Button>
          <Button disabled={!valid} className="bg-blue-600 hover:bg-blue-500" data-testid="line-save"
                  onClick={() => onSave({ label, kind, ref_id: kind === "products" ? null : refId, product_ids: kind === "products" ? multi : [] })}>
            Kaydet
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
